import React, { useState, useEffect, useMemo } from 'react';
import LZString from 'lz-string';

const StockSearchTable = ({ 
    inventory, 
    FIXED_VARIANTS, 
    coreProducts, 
    addToCore, 
    removeFromCore,
    variantSales
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(50);
    const [selectedVariants, setSelectedVariants] = useState([]);

    // Get all unique variant titles from the inventory
    const allVariants = useMemo(() => {
        const variantSet = new Set();
        inventory.forEach(product => {
            product.variants.forEach(variant => {
                variantSet.add(variant.title);
            });
        });
        return Array.from(variantSet).sort();
    }, [inventory]);

    const handleSearchInput = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);
        
        if (!term.trim()) {
            // Get unique products when clearing search
            const uniqueInventory = Array.from(
                new Map(inventory.map(item => [item.product_id, item])).values()
            );
            setFilteredInventory(uniqueInventory);
            setCurrentPage(1);
            return;
        }
        
        const filtered = inventory.filter(product => 
            product.product_title.toLowerCase().includes(term) || 
            product.variants.some(variant => variant.sku?.toLowerCase().includes(term))
        );
        
        // Get unique filtered products
        const uniqueFiltered = Array.from(
            new Map(filtered.map(item => [item.product_id, item])).values()
        );
        setFilteredInventory(uniqueFiltered);
        setCurrentPage(1);
    };

    const paginateData = (data) => {
        const indexOfLastRow = currentPage * rowsPerPage;
        const indexOfFirstRow = indexOfLastRow - rowsPerPage;
        return data.slice(indexOfFirstRow, indexOfLastRow);
    };

    const Pagination = ({ totalItems }) => {
        const totalPages = Math.ceil(totalItems / rowsPerPage);
        
        return (
            <div className="pagination">
                <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-button"
                >
                    Previous
                </button>
                <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                </span>
                <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                >
                    Next
                </button>
            </div>
        );
    };

    useEffect(() => {
        if (inventory?.length > 0) {
            const uniqueInventory = Array.from(
                new Map(inventory.map(item => [item.product_id, item])).values()
            );
            setFilteredInventory(uniqueInventory);
            setCurrentPage(1);
            setSearchTerm('');
        }
    }, [inventory]);

    // Handle variant selection
    const handleVariantToggle = (variantTitle) => {
        setSelectedVariants(prev => {
            if (prev.includes(variantTitle)) {
                return prev.filter(v => v !== variantTitle);
            }
            return [...prev, variantTitle];
        });
    };

    // Reset selected variants when inventory changes
    useEffect(() => {
        setSelectedVariants(allVariants.slice(0, 4)); // Default to first 4 variants
    }, [allVariants]);

    const formatPrice = (price) => {
        return `${parseFloat(price).toFixed(2)} RON`;
    };

    const ProductInfo = ({ product }) => {
        const variant = product.variants[0];
        const hasDiscount = variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price);
        
        return (
            <div className="product-info">
                <div className="product-title">{product.product_title}</div>
                <div className="product-details">
                    <span className="product-sku">
                        {variant.sku}
                        {hasDiscount ? (
                            <span className="price-info">
                                <span className="compare-price">{formatPrice(variant.compare_at_price)}</span>
                                {' → '}
                                <span className="current-price">{formatPrice(variant.price)}</span>
                            </span>
                        ) : (
                            <span className="price-info">
                                <span className="current-price">{formatPrice(variant.price)}</span>
                            </span>
                        )}
                    </span>
                </div>
            </div>
        );
    };

    // Update the styles
    const styles = `
    .product-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .product-details {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    .product-sku {
        display: flex;
        gap: 8px;
        align-items: center;
        color: #666;
        font-size: 12px;
    }

    .price-info {
        margin-left: 8px;
    }

    .compare-price {
        color: #dc3545;
        text-decoration: line-through;
    }

    .current-price {
        color: #2c3e50;
        font-weight: 500;
    }
    `;

    // Add the styles to the document
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    return (
        <>
            <div className="controls-container">
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Search by product name or SKU..."
                        value={searchTerm}
                        onChange={(e) => handleSearchInput(e)}
                        className="search-input"
                    />
                </div>

                <div className="variant-selector">
                    <h4>Select Variants to Display:</h4>
                    <div className="variant-options">
                        {allVariants.map(variant => (
                            <label key={variant} className="variant-checkbox">
                                <input
                                    type="checkbox"
                                    checked={selectedVariants.includes(variant)}
                                    onChange={() => handleVariantToggle(variant)}
                                />
                                {variant}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="inventory-table-container">
                <table className="inventory-table">
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Product Name</th>
                            {selectedVariants.map(variant => (
                                <th key={variant}>{variant}</th>
                            ))}
                            <th>Total Stock</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginateData(filteredInventory).map((product) => {
                            const totalStock = product.variants.reduce(
                                (sum, v) => sum + (v.inventory_quantity || 0), 
                                0
                            );
                            
                            return (
                                <tr key={product.product_id}>
                                    <td className="image-cell">
                                        {product.image_url ? (
                                            <img 
                                                src={product.image_url} 
                                                alt={product.product_title}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <div className="no-image">No Image</div>
                                        )}
                                    </td>
                                    <td>
                                        <ProductInfo product={product} />
                                    </td>
                                    {selectedVariants.map(variantTitle => {
                                        const variant = product.variants.find(v => v.title === variantTitle);
                                        const salesKey = variant ? `${product.product_id}-${variant.id}` : null;
                                        const salesCount = salesKey ? variantSales[salesKey] || 0 : 0;
                                        
                                        return (
                                            <td key={variantTitle} className="variant-cell">
                                                {variant ? (
                                                    <>
                                                        <div>Stock: {variant.inventory_quantity}</div>
                                                        <div>Sold: {salesCount}</div>
                                                    </>
                                                ) : '-'}
                                            </td>
                                        );
                                    })}
                                    <td>{totalStock}</td>
                                    <td>
                                        {coreProducts[product.product_id] ? (
                                            <button 
                                                onClick={() => removeFromCore(product)}
                                                className="core-button remove"
                                            >
                                                Remove from Core
                                            </button>
                                        ) : (
                                            <button 
                                                onClick={() => addToCore(product)}
                                                className="core-button"
                                            >
                                                Add to Core
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <Pagination totalItems={filteredInventory.length} />
            </div>
        </>
    );
};

export default StockSearchTable; 